import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderKanban, Package, Rocket, Code2, ArrowRight } from 'lucide-react';
import { useAuthStore } from '../../stores/useAuthStore';
import { useProjectStore } from '../../stores/useProjectStore';
import { useAccountStore } from '../../stores/useAccountStore';
import { useWalletStore } from '../../stores/useWalletStore';
import { rivellumApi } from '../../api/rivellumApi';
import type { BalanceResponse, ChainInfo } from '../../api/rivellumApi';
import s from './Pages.module.css';

export function HomePage() {
  const navigate = useNavigate();
  const address = useAuthStore(a => a.address);
  const walletAddress = useWalletStore(w => w.activeAccount?.address);
  const projects = useProjectStore(p => p.projects);
  const deployments = useAccountStore(a => a.deployments);
  const activities = useAccountStore(a => a.activities);

  const [balance, setBalance] = useState<BalanceResponse | null>(null);
  const [chainInfo, setChainInfo] = useState<ChainInfo | null>(null);
  const [nodeOnline, setNodeOnline] = useState<boolean | null>(null);

  const effectiveAddr = walletAddress || address;

  useEffect(() => {
    rivellumApi.health()
      .then(() => setNodeOnline(true))
      .catch(() => setNodeOnline(false));
    rivellumApi.getChainInfo()
      .then(setChainInfo)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!effectiveAddr) return;
    rivellumApi.getBalance(effectiveAddr)
      .then(setBalance)
      .catch(() => {});
  }, [effectiveAddr]);

  const shortAddr = effectiveAddr ? `${effectiveAddr.slice(0, 8)}...${effectiveAddr.slice(-6)}` : '';

  const formatBalance = (raw: string | undefined) => {
    if (!raw) return '0';
    const n = BigInt(raw);
    const whole = n / 1_000_000_000n;
    const frac = n % 1_000_000_000n;
    if (frac === 0n) return whole.toLocaleString();
    return `${whole.toLocaleString()}.${frac.toString().padStart(9, '0').replace(/0+$/, '')}`;
  };

  return (
    <div className={s.page}>
      <div className={s.pageHeader}>
        <h1 className={s.pageTitle}>Welcome back{shortAddr ? `, ${shortAddr}` : ''}</h1>
        <p className={s.pageSubtitle}>Your Rivellum Console overview</p>
      </div>

      {/* Stats */}
      <div className={s.statsRow}>
        <div className={s.statCard}>
          <div className={s.statLabel}>Network</div>
          <div className={s.statValue}>
            {chainInfo ? chainInfo.chain_id : '—'}
            {nodeOnline !== null && (
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginLeft: 8, background: nodeOnline ? 'var(--color-success)' : 'var(--color-danger)' }} />
            )}
          </div>
        </div>
        <div className={s.statCard}>
          <div className={s.statLabel}>Balance ({chainInfo?.native_token || 'RIVL'})</div>
          <div className={s.statValue}>
            {balance ? formatBalance(balance.balances?.native?.spendable) : effectiveAddr ? '...' : '—'}
          </div>
        </div>
        <div className={s.statCard}>
          <div className={s.statLabel}>Deployments</div>
          <div className={s.statValue}>{deployments.length}</div>
        </div>
        <div className={s.statCard}>
          <div className={s.statLabel}>Activity</div>
          <div className={s.statValue}>{activities.length}</div>
        </div>
      </div>

      {/* Quick actions */}
      <div className={s.sectionHeader}>
        <h2 className={s.sectionTitle}>Quick Actions</h2>
      </div>
      <div className={s.cardGrid}>
        <button className={s.card} onClick={() => navigate('/projects')}>
          <div className={s.cardTitle}>
            <FolderKanban size={16} style={{ marginRight: 8, verticalAlign: -2 }} />
            Projects
          </div>
          <div className={s.cardDesc}>
            Create, manage, and organize your custom code workspaces and linked prefab deployments.
          </div>
          <div className={s.cardMeta}>
            <span className={s.badgeNeutral + ' ' + s.badge}>
              {projects.length} project{projects.length !== 1 ? 's' : ''}
            </span>
            <ArrowRight size={14} style={{ marginLeft: 'auto', color: 'var(--color-text-tertiary)' }} />
          </div>
        </button>

        <button className={s.card} onClick={() => navigate('/prefabs')}>
          <div className={s.cardTitle}>
            <Package size={16} style={{ marginRight: 8, verticalAlign: -2 }} />
            Prefabs
          </div>
          <div className={s.cardDesc}>
            Browse verified modules, configure instances through guided forms, and deploy with no code.
          </div>
          <div className={s.cardMeta}>
            <span className={`${s.badge} ${s.badgeSuccess}`}>No-code</span>
            <span className={`${s.badge} ${s.badgeInfo}`}>Verified</span>
            <ArrowRight size={14} style={{ marginLeft: 'auto', color: 'var(--color-text-tertiary)' }} />
          </div>
        </button>

        <button className={s.card} onClick={() => navigate('/ide')}>
          <div className={s.cardTitle}>
            <Code2 size={16} style={{ marginRight: 8, verticalAlign: -2 }} />
            IDE
          </div>
          <div className={s.cardDesc}>
            Write, edit, and deploy custom Mist contracts in the full-featured code editor.
          </div>
          <div className={s.cardMeta}>
            <span className={`${s.badge} ${s.badgeNeutral}`}>Code</span>
            <ArrowRight size={14} style={{ marginLeft: 'auto', color: 'var(--color-text-tertiary)' }} />
          </div>
        </button>

        <button className={s.card} onClick={() => navigate('/deployments')}>
          <div className={s.cardTitle}>
            <Rocket size={16} style={{ marginRight: 8, verticalAlign: -2 }} />
            Deployments
          </div>
          <div className={s.cardDesc}>
            View all deployments — custom code and prefab instances — with status, history, and details.
          </div>
          <div className={s.cardMeta}>
            <span className={s.badgeNeutral + ' ' + s.badge}>
              {deployments.length} deployment{deployments.length !== 1 ? 's' : ''}
            </span>
            <ArrowRight size={14} style={{ marginLeft: 'auto', color: 'var(--color-text-tertiary)' }} />
          </div>
        </button>
      </div>

      {/* Recent activity */}
      {activities.length > 0 && (
        <>
          <div className={s.sectionHeader} style={{ marginTop: 'var(--spacing-2xl)' }}>
            <h2 className={s.sectionTitle}>Recent Activity</h2>
            <button className={s.btnGhost} onClick={() => navigate('/activity')}>
              View all <ArrowRight size={14} />
            </button>
          </div>
          <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)' }}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Detail</th>
                  <th>Status</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {activities.slice(0, 5).map(ev => (
                  <tr key={ev.id}>
                    <td style={{ fontWeight: 500 }}>{ev.title}</td>
                    <td style={{ color: 'var(--color-text-secondary)' }}>{ev.detail}</td>
                    <td>
                      {ev.status && (
                        <span className={`${s.badge} ${
                          ev.status === 'success' ? s.badgeSuccess :
                          ev.status === 'failed' ? s.badgeDanger : s.badgeWarning
                        }`}>
                          {ev.status}
                        </span>
                      )}
                    </td>
                    <td style={{ color: 'var(--color-text-tertiary)', fontSize: 12 }}>
                      {new Date(ev.timestamp).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
